import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Search, FileDown, LogIn, LogOut, ShieldAlert, KeyRound } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";

const EVENTS = {
  LOGIN: { label: "Login", cls: "bg-green-100 text-green-800 border-green-300", icon: LogIn },
  LOGIN_FAILED: { label: "Failed login", cls: "bg-red-100 text-red-800 border-red-300", icon: ShieldAlert },
  LOGOUT: { label: "Logout", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: LogOut },
  OTP_SENT: { label: "OTP sent", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: KeyRound },
  PASSWORD_RESET_OTP: { label: "Password reset (OTP)", cls: "bg-blue-100 text-blue-800 border-blue-300", icon: KeyRound },
};
const ROLE = { SUPER_ADMIN: "Super Admin", TEMPLE_ADMIN: "Temple Admin", SUPERVISOR: "Supervisor", COUNTER_USER: "Counter User", VERIFICATION_USER: "Verification" };
const istDate = (d = new Date()) => new Date(d.getTime() + 5.5 * 3600e3).toISOString().slice(0, 10);
const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

// Super Admin / Temple Admin: who signed in, when, from which device and IP; failed attempts and logouts.
export default function LoginLogs() {
  const { user } = useAuth();
  const isAdmin = ["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user?.role);
  const [f, setF] = useState({ frm: istDate(new Date(Date.now() - 6 * 864e5)), to: istDate(), q: "", action: "", temple_id: "" });
  const [data, setData] = useState(null);
  const [temples, setTemples] = useState([]);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const load = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
    setBusy(true);
    api.get(`/login-logs?${p}`).then((r) => { setData(r.data); setPage(1); }).catch((e) => toast.error(fmtErr(e))).finally(() => setBusy(false));
  };
  useEffect(() => {
    if (!isAdmin) return;
    load();
    if (user.role === "SUPER_ADMIN") api.get("/temples").then((r) => setTemples(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  if (!isAdmin) return <Navigate to="/" replace />;

  const downloadPdf = () => {
    if (!data?.rows?.length) { toast.error("No data to export"); return; }
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text("Login Logs", 148, 14, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(`${f.frm || "…"} to ${f.to || "…"} · Generated ${new Date().toLocaleString("en-IN")}`, 148, 20, { align: "center" });
    autoTable(doc, {
      startY: 25,
      head: [["Date & Time", "Username", "Name", "Role", "Temple", "Event", "Device", "IP Address", "Note"]],
      body: data.rows.map((r) => [fmt(r.at), r.username, r.full_name, ROLE[r.role] || r.role, r.temple_id || "-", EVENTS[r.action]?.label || r.action, r.device || "-", r.ip || "-", r.reason || ""]),
      styles: { fontSize: 7.5, cellPadding: 1.4 },
      headStyles: { fillColor: [128, 0, 32], textColor: 255 },
    });
    doc.setFontSize(8);
    doc.text("Aatreya Infotech Systems - Temple ERP", 148, doc.internal.pageSize.getHeight() - 7, { align: "center" });
    doc.save(`LoginLogs_${f.frm}_${f.to}.pdf`);
  };

  const t = data?.totals || {};
  const pages = Math.max(1, Math.ceil((data?.rows?.length || 0) / PAGE_SIZE));
  const shown = (data?.rows || []).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  // Page buttons: first, last, and two either side of the current page, with … gaps.
  const pageList = [...new Set([1, pages, page - 2, page - 1, page, page + 1, page + 2])].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const Pager = () => pages > 1 && (
    <div className="flex flex-wrap items-center justify-between gap-2 mt-3" data-testid="logs-pager">
      <p className="text-xs text-slate-500">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.rows.length)} of {data.rows.length}</p>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>‹ Prev</Button>
        {pageList.map((n, i) => (
          <React.Fragment key={n}>
            {i > 0 && n - pageList[i - 1] > 1 && <span className="px-1 text-slate-400">…</span>}
            <button type="button" onClick={() => setPage(n)} data-testid={`logs-page-${n}`}
              className={`min-w-9 h-9 px-2 rounded-md text-sm font-semibold border ${n === page ? "bg-maroon-700 text-white border-maroon-700" : "bg-white text-maroon-800 border-[#E5D9C5] hover:bg-gold-50"}`}>{n}</button>
          </React.Fragment>
        ))}
        <Button size="sm" variant="outline" disabled={page === pages} onClick={() => setPage(page + 1)}>Next ›</Button>
      </div>
    </div>
  );
  return (
    <div data-testid="login-logs-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Login Logs</h1>
      <p className="text-sm text-slate-500 mb-4">Every sign-in with date and time, device and IP address — plus failed attempts, logouts and OTP password resets.</p>

      <Card className="shadow-sm mb-4">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div><Label className="text-xs">From Date</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="logs-from" /></div>
          <div><Label className="text-xs">To Date</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="logs-to" /></div>
          <div><Label className="text-xs">User</Label><Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Username or name" data-testid="logs-user" /></div>
          <div><Label className="text-xs">Event</Label>
            <Select value={f.action || "ALL"} onValueChange={(v) => setF({ ...f, action: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="logs-event"><SelectValue placeholder="All events" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All events</SelectItem>
                {Object.entries(EVENTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select></div>
          {user.role === "SUPER_ADMIN" ? (
            <div><Label className="text-xs">Temple</Label>
              <Select value={f.temple_id || "ALL"} onValueChange={(v) => setF({ ...f, temple_id: v === "ALL" ? "" : v })}>
                <SelectTrigger data-testid="logs-temple"><SelectValue placeholder="All temples" /></SelectTrigger>
                <SelectContent><SelectItem value="ALL">All temples</SelectItem>
                  {temples.map((x) => <SelectItem key={x.temple_id} value={x.temple_id}>{x.name}</SelectItem>)}</SelectContent>
              </Select></div>
          ) : <div />}
          <div className="flex gap-2">
            <Button onClick={load} disabled={busy} className="bg-maroon-700 hover:bg-maroon-800 text-white flex-1" data-testid="logs-search"><Search className="w-4 h-4 mr-1" /> View</Button>
            <Button variant="outline" onClick={downloadPdf} title="Download PDF" data-testid="logs-pdf"><FileDown className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[["LOGIN", "Logins", t.LOGIN], ["LOGIN_FAILED", "Failed attempts", t.LOGIN_FAILED], ["LOGOUT", "Logouts", t.LOGOUT]].map(([k, label, n]) => (
              <Card key={k} className="shadow-sm"><CardContent className="p-4 flex items-center gap-3">
                <span className={`w-10 h-10 rounded-lg border flex items-center justify-center ${EVENTS[k].cls}`}>{React.createElement(EVENTS[k].icon, { className: "w-5 h-5" })}</span>
                <div><p className="text-2xl font-bold text-maroon-900">{n || 0}</p><p className="text-xs text-slate-500">{label}</p></div>
              </CardContent></Card>
            ))}
          </div>
          <Card className="shadow-sm">
            <CardContent className="p-4 overflow-x-auto">
              <p className="text-xs text-slate-500 mb-2" data-testid="logs-count">{data.rows.length} record(s){data.rows.length >= 2000 ? " — showing the latest 2000; narrow the dates" : ""}</p>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
                  <th className="py-2 pr-4">Date &amp; Time</th><th className="py-2 pr-4">User</th><th className="py-2 pr-4">Role</th>
                  {user.role === "SUPER_ADMIN" && <th className="py-2 pr-4">Temple</th>}
                  <th className="py-2 pr-4">Event</th><th className="py-2 pr-4">Device</th><th className="py-2 pr-4">IP Address</th><th className="py-2">Note</th>
                </tr></thead>
                <tbody>
                  {data.rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">No login activity for the selected filters</td></tr>}
                  {shown.map((r) => {
                    const ev = EVENTS[r.action] || { label: r.action, cls: "bg-slate-100 text-slate-700 border-slate-300" };
                    return (
                      <tr key={r.id} className={`border-b border-[#F1EADB] ${r.action === "LOGIN_FAILED" ? "bg-red-50/50" : ""}`} data-testid={`log-row-${r.id}`}>
                        <td className="py-2 pr-4 whitespace-nowrap font-mono text-xs">{fmt(r.at)}</td>
                        <td className="py-2 pr-4"><p className="font-semibold text-maroon-900">{r.full_name || "-"}</p><p className="text-xs font-mono text-slate-500">{r.username}</p></td>
                        <td className="py-2 pr-4 text-xs">{ROLE[r.role] || r.role || "-"}</td>
                        {user.role === "SUPER_ADMIN" && <td className="py-2 pr-4 text-xs">{r.temple_id || "-"}</td>}
                        <td className="py-2 pr-4"><span className={`inline-block px-2 py-0.5 rounded border text-[11px] font-bold whitespace-nowrap ${ev.cls}`}>{ev.label}</span></td>
                        <td className="py-2 pr-4 text-xs whitespace-nowrap">{r.device || "-"}</td>
                        <td className="py-2 pr-4 text-xs font-mono">{r.ip || "-"}</td>
                        <td className="py-2 text-xs text-slate-500">{r.reason || ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pager />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
